import { useState } from 'react';
import { X, Flag, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/toast';

const USER_REPORT_REASONS = [
  { value: 'suspected_scam', label: 'Suspected scam' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'harassment', label: 'Harassment or abuse' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'other', label: 'Other' },
];

export function ReportUserModal({ isOpen, onClose, reportedUserId, reportedUsername }) {
  const { api } = useAuth();
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast.error('Please select a reason for reporting');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reports/user', {
        reported_user_id: reportedUserId,
        reason: selectedReason,
        details: details || null,
      });

      toast.success('Report submitted. Thank you for helping keep WANTD safe.');
      onClose();
      setSelectedReason('');
      setDetails('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit report';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="report-user-modal"
    >
      <div className="bg-[var(--theme-card)] rounded-2xl w-full max-w-md border border-[var(--theme-border)] shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--theme-border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
              <Flag className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[var(--theme-text-primary)]">Report user</h2>
              <p className="text-xs text-[var(--theme-text-muted)] truncate max-w-[200px]">@{reportedUsername}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors"
            data-testid="close-report-user-modal"
          >
            <X className="w-5 h-5 text-[var(--theme-text-secondary)]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-3">
              Why are you reporting this user?
            </label>
            <div className="space-y-2">
              {USER_REPORT_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedReason === reason.value
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/10'
                      : 'border-[var(--theme-border)] hover:border-[var(--theme-text-muted)] hover:bg-[var(--theme-hover)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="user-report-reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="w-4 h-4 text-red-500 focus:ring-red-500 border-[var(--theme-border)]"
                    data-testid={`user-reason-${reason.value}`}
                  />
                  <span className={`text-sm ${
                    selectedReason === reason.value
                      ? 'text-[var(--theme-text-primary)] font-medium'
                      : 'text-[var(--theme-text-secondary)]'
                  }`}>
                    {reason.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--theme-text-primary)] mb-2">
              Additional details <span className="text-[var(--theme-text-muted)] font-normal">(optional)</span>
            </label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe what happened or provide any context that might help us review..."
              className="bg-[var(--theme-bg)] border-[var(--theme-border)] resize-none"
              rows={3}
              maxLength={500}
              data-testid="user-report-details-input"
            />
            <p className="text-xs text-[var(--theme-text-muted)] mt-1 text-right">
              {details.length}/500
            </p>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              False reports may result in restrictions on your account. Only report genuine concerns.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 p-4 border-t border-[var(--theme-border)]">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-[var(--theme-border)]"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            data-testid="submit-user-report-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Flag className="w-4 h-4 mr-2" />
                Submit Report
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ReportUserModal;
